// src/api/portfolio.js
import axios from "axios";

const API_BASE = "/portfolio";

export async function getPortfolioConcentration() {
  const response = await axios.get(`${API_BASE}/concentration`);
  return response.data?.concentration ?? {};
}

export async function getPortfolioPL() {
  const response = await axios.get(`${API_BASE}/pl`);
  return response.data?.pl ?? {};
}

export async function getPortfolio() {
  // Use /positions endpoint for current positions
  const response = await axios.get(`${API_BASE}/positions`);
  return response.data;
}

export async function getPortfolioAnalytics() {
  const response = await axios.get(`${API_BASE}/analytics`);
  return response.data;
}

export async function getPortfolioHistory() {
  const response = await axios.get(`${API_BASE}/history`);
  return response.data?.history ?? [];
}

export async function getPortfolioRiskChecks() {
  const response = await axios.get(`${API_BASE}/risk-checks`);
  return response.data?.risk_summary ?? {};
}

export async function getPortfolioExposure() {
  const response = await axios.get(`${API_BASE}/exposure`);
  return response.data?.exposure ?? {};
}

export async function getRuntimeMaintenanceStatus() {
  const response = await axios.get(`${API_BASE}/runtime-maintenance/status`);
  return response.data?.maintenance ?? {};
}

export async function runRuntimeMaintenance(compact = false) {
  const response = await axios.post(`${API_BASE}/runtime-maintenance`, {
    compact: Boolean(compact),
  });
  return response.data ?? {};
}

export async function backfillPortfolioHistory(days = 90) {
  const response = await axios.post(`${API_BASE}/history/backfill`, null, {
    params: { days: Number(days) },
  });
  return response.data ?? {};
}

export async function exportPortfolioHistoryCsv(days = 90) {
  const response = await axios.get(`${API_BASE}/history/export`, {
    params: { days: Number(days) },
    responseType: "blob",
  });

  const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
  const contentDisposition = response.headers?.["content-disposition"] || "";
  const match = contentDisposition.match(/filename=([^;]+)/i);
  const filename = match ? match[1].replace(/"/g, "").trim() : `equity_history_${Number(days)}d.csv`;

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  return { filename };
}

export async function getReadinessGate() {
  const response = await axios.get(`${API_BASE}/readiness-gate`);
  return response.data ?? {};
}

export async function snapshotReadinessGate() {
  const response = await axios.post(`${API_BASE}/readiness-gate/snapshot`);
  return response.data ?? {};
}

export async function getReadinessGateHistory(limit = 20) {
  const response = await axios.get(`${API_BASE}/readiness-gate/history?limit=${Number(limit)}`);
  return response.data ?? {};
}

export async function getReadinessGateTrend(limit = 30) {
  const response = await axios.get(`${API_BASE}/readiness-gate/trend?limit=${Number(limit)}`);
  return response.data ?? {};
}

export async function getReadinessAlerts(limit = 20) {
  const response = await axios.get(`${API_BASE}/readiness-alerts?limit=${Number(limit)}`);
  return response.data ?? {};
}

export async function getReadinessActions(limit = 20) {
  const response = await axios.get(`${API_BASE}/readiness-actions?limit=${Number(limit)}`);
  return response.data ?? {};
}

export async function sendReadinessTestAlert(sendWebhook = false) {
  const response = await axios.post(`${API_BASE}/readiness-alerts/test`, {
    send_webhook: Boolean(sendWebhook),
  });
  return response.data ?? {};
}

export async function getPortfolioAnomalySignals() {
  const response = await axios.get(`${API_BASE}/anomaly-signals`);
  return response.data ?? {};
}

export async function syncBrokerHistory(days = 30) {
  const response = await axios.post(`${API_BASE}/history/sync-broker`, {
    days: Number(days),
  });
  return response.data ?? {};
}

export async function getBrokerSyncStatus() {
  const response = await axios.get(`${API_BASE}/history/sync-broker/status`);
  return response.data?.sync ?? {};
}

export async function simulatePortfolioTrade({ symbol, quantity, price, action = "buy", sleeve = "what-if" }) {
  const response = await axios.post(`${API_BASE}/simulate`, {
    symbol,
    quantity: Number(quantity),
    price: Number(price),
    action,
    sleeve,
  });
  return response.data?.simulation ?? {};
}

export async function compareWhatIfScenarios({ scenarioA, scenarioB }) {
  const response = await axios.post(`${API_BASE}/simulate/compare`, {
    scenario_a: {
      ...scenarioA,
      quantity: Number(scenarioA.quantity),
      price: Number(scenarioA.price),
    },
    scenario_b: {
      ...scenarioB,
      quantity: Number(scenarioB.quantity),
      price: Number(scenarioB.price),
    },
  });
  return response.data?.comparison ?? {};
}

export async function getWhatIfCompareHistory(options = 20) {
  const normalized = typeof options === "number" ? { limit: options } : options;
  const params = {
    limit: Number(normalized?.limit ?? 20),
  };
  if (normalized?.presetALabel) params.preset_a_label = String(normalized.presetALabel).trim();
  if (normalized?.presetBLabel) params.preset_b_label = String(normalized.presetBLabel).trim();
  if (normalized?.fromAsOf) params.from_as_of = String(normalized.fromAsOf).trim();
  if (normalized?.toAsOf) params.to_as_of = String(normalized.toAsOf).trim();
  if (normalized?.window) params.window = String(normalized.window).trim();
  if (normalized?.sortBy) params.sort_by = String(normalized.sortBy).trim();
  if (normalized?.sortOrder) params.sort_order = String(normalized.sortOrder).trim();
  if (normalized?.page) params.page = Number(normalized.page);
  if (normalized?.pageSize) params.page_size = Number(normalized.pageSize);
  if (normalized?.noteContains) params.note_contains = String(normalized.noteContains).trim();
  if (normalized?.minDeltaMarketValue !== undefined && normalized?.minDeltaMarketValue !== null && normalized?.minDeltaMarketValue !== "") {
    params.min_delta_market_value = Number(normalized.minDeltaMarketValue);
  }
  if (normalized?.minDeltaUnrealizedPl !== undefined && normalized?.minDeltaUnrealizedPl !== null && normalized?.minDeltaUnrealizedPl !== "") {
    params.min_delta_unrealized_pl = Number(normalized.minDeltaUnrealizedPl);
  }
  const response = await axios.get(`${API_BASE}/simulate/compare/history`, {
    params,
  });
  const history = response.data?.history ?? [];
  const count = Number(response.data?.count ?? history.length);
  return {
    history,
    appliedFilters: response.data?.applied_filters ?? {},
    totalCount: Number(response.data?.total_count ?? count),
    filteredCount: Number(response.data?.filtered_count ?? count),
    count,
    page: Number(response.data?.page ?? 1),
    pageSize: Number(response.data?.page_size ?? 20),
    totalPages: Number(response.data?.total_pages ?? 1),
  };
}

export async function getWhatIfCompareFilterPresets(tag) {
  const params = {};
  if (tag != null && String(tag).trim()) {
    params.tag = String(tag).trim();
  }
  const response = await axios.get(`${API_BASE}/simulate/compare/history/filter-presets`, { params });
  return response.data?.presets ?? [];
}

export async function getWhatIfCompareFilterPresetTags() {
  const response = await axios.get(`${API_BASE}/simulate/compare/history/filter-presets/tags`);
  return response.data?.tags ?? [];
}

export async function saveWhatIfCompareFilterPresets(presets) {
  const response = await axios.post(`${API_BASE}/simulate/compare/history/filter-presets`, {
    presets,
  });
  return response.data?.presets ?? [];
}

export async function upsertWhatIfCompareFilterPreset(preset) {
  const response = await axios.post(`${API_BASE}/simulate/compare/history/filter-presets/upsert`, {
    ...preset,
  });
  return response.data ?? {};
}

export async function deleteWhatIfCompareFilterPreset(name) {
  const response = await axios.delete(
    `${API_BASE}/simulate/compare/history/filter-presets/${encodeURIComponent(String(name || "").trim())}`
  );
  return response.data ?? {};
}

export async function getWhatIfCompareFilterPresetUsage(options = {}) {
  const params = {
    include_zero_usage: options?.includeZeroUsage !== undefined ? Boolean(options.includeZeroUsage) : true,
    view_mode: options?.viewMode ? String(options.viewMode).trim() : "most_used",
  };
  if (options?.tag != null && String(options.tag).trim()) {
    params.tag = String(options.tag).trim();
  }
  const response = await axios.get(`${API_BASE}/simulate/compare/history/filter-presets/usage`, {
    params,
  });
  return response.data?.usage ?? [];
}

export async function trackWhatIfCompareFilterPresetUsage(name, action) {
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/filter-presets/${encodeURIComponent(String(name || "").trim())}/usage`,
    { action: String(action || "").trim() }
  );
  return response.data ?? {};
}

export async function getWhatIfCompareFilterPresetUsageSettings() {
  const response = await axios.get(`${API_BASE}/simulate/compare/history/filter-presets/usage/settings`);
  return {
    enabled: Boolean(response.data?.enabled ?? true),
  };
}

export async function saveWhatIfCompareFilterPresetUsageSettings(enabled) {
  const response = await axios.post(`${API_BASE}/simulate/compare/history/filter-presets/usage/settings`, {
    enabled: Boolean(enabled),
  });
  return {
    enabled: Boolean(response.data?.enabled ?? true),
  };
}

export async function resetWhatIfCompareFilterPresetUsage(name) {
  const normalizedName = String(name || "").trim();
  const endpoint = normalizedName
    ? `${API_BASE}/simulate/compare/history/filter-presets/${encodeURIComponent(normalizedName)}/usage/reset`
    : `${API_BASE}/simulate/compare/history/filter-presets/usage/reset`;
  const response = await axios.post(endpoint);
  return response.data ?? {};
}

export async function deleteWhatIfCompareFilterPresetsUnused(tag) {
  const params = {};
  if (tag != null && String(tag).trim()) {
    params.tag = String(tag).trim();
  }
  const response = await axios.delete(`${API_BASE}/simulate/compare/history/filter-presets/unused`, { params });
  return response.data ?? {};
}

export async function deleteWhatIfCompareFilterPresetsUnusedSince(unusedSince, tag) {
  const params = {
    unused_since: String(unusedSince || "").trim(),
  };
  if (tag != null && String(tag).trim()) {
    params.tag = String(tag).trim();
  }
  const response = await axios.delete(`${API_BASE}/simulate/compare/history/filter-presets/unused-since`, {
    params,
  });
  return response.data ?? {};
}

export async function getWhatIfCompareFilterPresetArchive() {
  const response = await axios.get(`${API_BASE}/simulate/compare/history/filter-presets/archive`);
  return response.data?.archive ?? [];
}

export async function restoreWhatIfCompareFilterPreset(name) {
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/filter-presets/archive/${encodeURIComponent(String(name || "").trim())}/restore`
  );
  return response.data ?? {};
}

export async function purgeWhatIfCompareFilterPresetArchive(deletedBefore) {
  const params = deletedBefore ? { deleted_before: String(deletedBefore).trim() } : {};
  const response = await axios.delete(`${API_BASE}/simulate/compare/history/filter-presets/archive`, { params });
  return response.data ?? {};
}

export async function exportWhatIfCompareFilterPresets(tag) {
  const params = {};
  if (tag != null && String(tag).trim()) {
    params.tag = String(tag).trim();
  }
  const response = await axios.get(`${API_BASE}/simulate/compare/history/filter-presets/export`, {
    params,
    responseType: 'blob',
  });
  return response.data;
}

export async function getWhatIfCompareFilterExportProfiles() {
  const response = await axios.get(`${API_BASE}/simulate/compare/history/filter-presets/export-profiles`);
  return response.data?.profiles ?? [];
}

export async function exportWhatIfCompareFilterExportProfiles() {
  const response = await axios.get(`${API_BASE}/simulate/compare/history/filter-presets/export-profiles/export`, {
    responseType: 'blob',
  });
  return response.data;
}

export async function importWhatIfCompareFilterExportProfiles(profiles, mode = 'merge') {
  const response = await axios.post(`${API_BASE}/simulate/compare/history/filter-presets/export-profiles/import`, {
    profiles,
    mode,
  });
  return response.data ?? {};
}

export async function getWhatIfCompareFilterExportProfileArchive() {
  const response = await axios.get(`${API_BASE}/simulate/compare/history/filter-presets/export-profiles/archive`);
  return response.data?.archive ?? [];
}

export async function upsertWhatIfCompareFilterExportProfile(profile) {
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/filter-presets/export-profiles`,
    profile,
  );
  return response.data ?? {};
}

export async function deleteWhatIfCompareFilterExportProfile(name) {
  const response = await axios.delete(
    `${API_BASE}/simulate/compare/history/filter-presets/export-profiles/${encodeURIComponent(String(name || "").trim())}`,
  );
  return response.data ?? {};
}

export async function cloneWhatIfCompareFilterExportProfile(name, newName) {
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/filter-presets/export-profiles/${encodeURIComponent(String(name || "").trim())}/clone`,
    { new_name: newName },
  );
  return response.data ?? {};
}

export async function renameWhatIfCompareFilterExportProfile(name, newName) {
  const response = await axios.patch(
    `${API_BASE}/simulate/compare/history/filter-presets/export-profiles/${encodeURIComponent(String(name || "").trim())}`,
    { new_name: newName },
  );
  return response.data ?? {};
}

export async function restoreWhatIfCompareFilterExportProfile(name) {
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/filter-presets/export-profiles/archive/${encodeURIComponent(String(name || "").trim())}/restore`
  );
  return response.data ?? {};
}

export async function purgeWhatIfCompareFilterExportProfileArchive(deletedBefore) {
  const params = deletedBefore ? { deleted_before: String(deletedBefore).trim() } : {};
  const response = await axios.delete(`${API_BASE}/simulate/compare/history/filter-presets/export-profiles/archive`, { params });
  return response.data ?? {};
}

export async function importWhatIfCompareFilterPresets(presets, mode = 'merge', options = {}) {
  const payload = {
    presets,
    mode,
  };
  if (options?.importTag != null && String(options.importTag).trim()) {
    payload.import_tag = String(options.importTag).trim();
    payload.import_tag_mode = options?.importTagMode ? String(options.importTagMode).trim() : 'append';
  }
  if (options?.strictImport === true) {
    payload.strict = true;
  }
  if (options?.dryRun === true) {
    payload.dry_run = true;
  }
  if (options?.replaceConfirmed === true) {
    payload.replace_confirmed = true;
  }
  const response = await axios.post(`${API_BASE}/simulate/compare/history/filter-presets/import`, {
    ...payload,
  });
  return response.data ?? {};
}

export async function bulkTagWhatIfCompareFilterPresets(action, tag, options = {}) {
  const payload = { action, tag };
  if (options?.replaceWith != null && String(options.replaceWith).trim()) {
    payload.replace_with = String(options.replaceWith).trim();
  }
  if (options?.filterTag != null && String(options.filterTag).trim()) {
    payload.filter_tag = String(options.filterTag).trim();
  }
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/filter-presets/bulk-tag`,
    payload,
  );
  return response.data ?? {};
}

export async function cloneWhatIfCompareFilterPreset(name, newName) {
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/filter-presets/${encodeURIComponent(name)}/clone`,
    { new_name: newName },
  );
  return response.data ?? {};
}

export async function renameWhatIfCompareFilterPreset(name, newName) {
  const response = await axios.patch(
    `${API_BASE}/simulate/compare/history/filter-presets/${encodeURIComponent(name)}`,
    { new_name: newName },
  );
  return response.data ?? {};
}

export async function cloneWhatIfCompareSortPreset(name, newName) {
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/sort-presets/${encodeURIComponent(name)}/clone`,
    { new_name: newName },
  );
  return response.data ?? {};
}

export async function renameWhatIfCompareSortPreset(name, newName) {
  const response = await axios.patch(
    `${API_BASE}/simulate/compare/history/sort-presets/${encodeURIComponent(name)}`,
    { new_name: newName },
  );
  return response.data ?? {};
}

export async function cloneWhatIfCompareViewPreset(name, newName) {
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/view-presets/${encodeURIComponent(name)}/clone`,
    { new_name: newName },
  );
  return response.data ?? {};
}

export async function renameWhatIfCompareViewPreset(name, newName) {
  const response = await axios.patch(
    `${API_BASE}/simulate/compare/history/view-presets/${encodeURIComponent(name)}`,
    { new_name: newName },
  );
  return response.data ?? {};
}

export async function cloneWhatIfCompareExportProfile(name, newName) {
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/export-profiles/${encodeURIComponent(name)}/clone`,
    { new_name: newName },
  );
  return response.data ?? {};
}

export async function renameWhatIfCompareExportProfile(name, newName) {
  const response = await axios.patch(
    `${API_BASE}/simulate/compare/history/export-profiles/${encodeURIComponent(name)}`,
    { new_name: newName },
  );
  return response.data ?? {};
}

export async function getWhatIfCompareExportProfileArchive() {
  const response = await axios.get(`${API_BASE}/simulate/compare/history/export-profiles/archive`);
  return response.data ?? {};
}

export async function restoreWhatIfCompareExportProfile(name) {
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/export-profiles/archive/${encodeURIComponent(name)}/restore`,
  );
  return response.data ?? {};
}

export async function purgeWhatIfCompareExportProfileArchive(deletedBefore) {
  const params = deletedBefore ? { deleted_before: deletedBefore } : {};
  const response = await axios.delete(
    `${API_BASE}/simulate/compare/history/export-profiles/archive`,
    { params },
  );
  return response.data ?? {};
}

export async function getWhatIfCompareSortPresets() {
  const response = await axios.get(`${API_BASE}/simulate/compare/history/sort-presets`);
  return response.data?.presets ?? [];
}

export async function saveWhatIfCompareSortPresets(presets) {
  const response = await axios.post(`${API_BASE}/simulate/compare/history/sort-presets`, {
    presets,
  });
  return response.data?.presets ?? [];
}

export async function upsertWhatIfCompareSortPreset(preset) {
  const response = await axios.post(`${API_BASE}/simulate/compare/history/sort-presets/upsert`, {
    ...preset,
  });
  return response.data ?? {};
}

export async function deleteWhatIfCompareSortPreset(name) {
  const response = await axios.delete(
    `${API_BASE}/simulate/compare/history/sort-presets/${encodeURIComponent(String(name || "").trim())}`
  );
  return response.data ?? {};
}

export async function getWhatIfCompareSortPresetArchive() {
  const response = await axios.get(`${API_BASE}/simulate/compare/history/sort-presets/archive`);
  return response.data ?? {};
}

export async function restoreWhatIfCompareSortPreset(name) {
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/sort-presets/archive/${encodeURIComponent(name)}/restore`,
  );
  return response.data ?? {};
}

export async function purgeWhatIfCompareSortPresetArchive(deletedBefore) {
  const params = deletedBefore ? { deleted_before: deletedBefore } : {};
  const response = await axios.delete(
    `${API_BASE}/simulate/compare/history/sort-presets/archive`,
    { params },
  );
  return response.data ?? {};
}

export async function getWhatIfCompareViewPresets() {
  const response = await axios.get(`${API_BASE}/simulate/compare/history/view-presets`);
  return response.data?.presets ?? [];
}

export async function saveWhatIfCompareViewPresets(presets) {
  const response = await axios.post(`${API_BASE}/simulate/compare/history/view-presets`, {
    presets,
  });
  return response.data?.presets ?? [];
}

export async function upsertWhatIfCompareViewPreset(preset) {
  const response = await axios.post(`${API_BASE}/simulate/compare/history/view-presets/upsert`, {
    ...preset,
  });
  return response.data ?? {};
}

export async function deleteWhatIfCompareViewPreset(name) {
  const response = await axios.delete(
    `${API_BASE}/simulate/compare/history/view-presets/${encodeURIComponent(String(name || "").trim())}`
  );
  return response.data ?? {};
}

export async function getWhatIfCompareViewPresetArchive() {
  const response = await axios.get(`${API_BASE}/simulate/compare/history/view-presets/archive`);
  return response.data ?? {};
}

export async function restoreWhatIfCompareViewPreset(name) {
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/view-presets/archive/${encodeURIComponent(name)}/restore`,
  );
  return response.data ?? {};
}

export async function purgeWhatIfCompareViewPresetArchive(deletedBefore) {
  const params = deletedBefore ? { deleted_before: deletedBefore } : {};
  const response = await axios.delete(
    `${API_BASE}/simulate/compare/history/view-presets/archive`,
    { params },
  );
  return response.data ?? {};
}

export async function getWhatIfCompareSortPresetUsage(options = {}) {
  const params = {
    include_zero_usage: options?.includeZeroUsage !== undefined ? Boolean(options.includeZeroUsage) : true,
    view_mode: options?.viewMode ? String(options.viewMode).trim() : "most_used",
  };
  const response = await axios.get(`${API_BASE}/simulate/compare/history/sort-presets/usage`, { params });
  return response.data?.usage ?? [];
}

export async function trackWhatIfCompareSortPresetUsage(name, action) {
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/sort-presets/${encodeURIComponent(String(name || "").trim())}/usage`,
    { action: String(action || "").trim() }
  );
  return response.data ?? {};
}

export async function getWhatIfCompareSortPresetUsageSettings() {
  const response = await axios.get(`${API_BASE}/simulate/compare/history/sort-presets/usage/settings`);
  return { enabled: Boolean(response.data?.enabled ?? true) };
}

export async function saveWhatIfCompareSortPresetUsageSettings(enabled) {
  const response = await axios.post(`${API_BASE}/simulate/compare/history/sort-presets/usage/settings`, {
    enabled: Boolean(enabled),
  });
  return { enabled: Boolean(response.data?.enabled ?? true) };
}

export async function resetWhatIfCompareSortPresetUsage(name) {
  const normalizedName = String(name || "").trim();
  const endpoint = normalizedName
    ? `${API_BASE}/simulate/compare/history/sort-presets/${encodeURIComponent(normalizedName)}/usage/reset`
    : `${API_BASE}/simulate/compare/history/sort-presets/usage/reset`;
  const response = await axios.post(endpoint);
  return response.data ?? {};
}

export async function getWhatIfCompareViewPresetUsage(options = {}) {
  const params = {
    include_zero_usage: options?.includeZeroUsage !== undefined ? Boolean(options.includeZeroUsage) : true,
    view_mode: options?.viewMode ? String(options.viewMode).trim() : "most_used",
  };
  const response = await axios.get(`${API_BASE}/simulate/compare/history/view-presets/usage`, { params });
  return response.data?.usage ?? [];
}

export async function trackWhatIfCompareViewPresetUsage(name, action) {
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/view-presets/${encodeURIComponent(String(name || "").trim())}/usage`,
    { action: String(action || "").trim() }
  );
  return response.data ?? {};
}

export async function getWhatIfCompareViewPresetUsageSettings() {
  const response = await axios.get(`${API_BASE}/simulate/compare/history/view-presets/usage/settings`);
  return { enabled: Boolean(response.data?.enabled ?? true) };
}

export async function saveWhatIfCompareViewPresetUsageSettings(enabled) {
  const response = await axios.post(`${API_BASE}/simulate/compare/history/view-presets/usage/settings`, {
    enabled: Boolean(enabled),
  });
  return { enabled: Boolean(response.data?.enabled ?? true) };
}

export async function resetWhatIfCompareViewPresetUsage(name) {
  const normalizedName = String(name || "").trim();
  const endpoint = normalizedName
    ? `${API_BASE}/simulate/compare/history/view-presets/${encodeURIComponent(normalizedName)}/usage/reset`
    : `${API_BASE}/simulate/compare/history/view-presets/usage/reset`;
  const response = await axios.post(endpoint);
  return response.data ?? {};
}

export async function exportWhatIfCompareSortPresets() {
  const response = await axios.get(
    `${API_BASE}/simulate/compare/history/sort-presets/export`,
    { responseType: 'blob' },
  );
  return response.data;
}

export async function importWhatIfCompareSortPresets(presets, mode = 'merge', options = {}) {
  const payload = { presets, mode };
  if (options?.dryRun === true) payload.dry_run = true;
  if (options?.replaceConfirmed === true) payload.replace_confirmed = true;
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/sort-presets/import`,
    payload,
  );
  return response.data ?? {};
}

export async function exportWhatIfCompareViewPresets() {
  const response = await axios.get(
    `${API_BASE}/simulate/compare/history/view-presets/export`,
    { responseType: 'blob' },
  );
  return response.data;
}

export async function importWhatIfCompareViewPresets(presets, mode = 'merge', options = {}) {
  const payload = { presets, mode };
  if (options?.dryRun === true) payload.dry_run = true;
  if (options?.replaceConfirmed === true) payload.replace_confirmed = true;
  const response = await axios.post(
    `${API_BASE}/simulate/compare/history/view-presets/import`,
    payload,
  );
  return response.data ?? {};
}

export async function getWhatIfCompareExportProfiles() {
  const response = await axios.get(`${API_BASE}/simulate/compare/history/export-profiles`);
  return response.data?.profiles ?? [];
}

export async function exportWhatIfCompareExportProfiles() {
  const response = await axios.get(`${API_BASE}/simulate/compare/history/export-profiles/export`, {
    responseType: 'blob',
  });
  return response.data;
}

export async function importWhatIfCompareExportProfiles(profiles, mode = 'merge', options = {}) {
  const payload = { profiles, mode };
  if (options?.replaceConfirmed === true) {
    payload.replace_confirmed = true;
  }
  const response = await axios.post(`${API_BASE}/simulate/compare/history/export-profiles/import`, {
    ...payload,
  });
  return response.data ?? {};
}

export async function saveWhatIfCompareExportProfiles(profiles) {
  const response = await axios.post(`${API_BASE}/simulate/compare/history/export-profiles`, {
    profiles,
  });
  return response.data?.profiles ?? [];
}

export async function upsertWhatIfCompareExportProfile(profile) {
  const response = await axios.post(`${API_BASE}/simulate/compare/history/export-profiles/upsert`, {
    ...profile,
  });
  return response.data ?? {};
}

export async function deleteWhatIfCompareExportProfile(name) {
  const response = await axios.delete(
    `${API_BASE}/simulate/compare/history/export-profiles/${encodeURIComponent(String(name || "").trim())}`
  );
  return response.data ?? {};
}

export async function getWhatIfCompareHistoryPositions(index) {
  const response = await axios.get(
    `${API_BASE}/simulate/compare/history/${Number(index)}/positions`
  );
  return {
    presetALabel: response.data?.preset_a_label ?? "",
    presetBLabel: response.data?.preset_b_label ?? "",
    positionsA: response.data?.positions_a ?? [],
    positionsB: response.data?.positions_b ?? [],
  };
}

export async function patchWhatIfCompareHistoryNote(index, note) {
  const response = await axios.patch(
    `${API_BASE}/simulate/compare/history/${Number(index)}/note`,
    { note: String(note ?? "") }
  );
  return {
    index: response.data?.index ?? index,
    note: response.data?.note ?? "",
  };
}

export async function appendWhatIfCompareHistory(entry) {
  const response = await axios.post(`${API_BASE}/simulate/compare/history`, {
    ...entry,
  });
  return response.data ?? {};
}

export async function exportWhatIfCompareHistoryCsv(options = 100) {
  const normalized = typeof options === "number" ? { limit: options } : options;
  const response = await axios.get(`${API_BASE}/simulate/compare/history/export`, {
    params: {
      limit: Number(normalized?.limit ?? 100),
      ...(normalized?.exportMode ? { export_mode: String(normalized.exportMode).trim() } : {}),
      ...(normalized?.page ? { page: Number(normalized.page) } : {}),
      ...(normalized?.pageSize ? { page_size: Number(normalized.pageSize) } : {}),
      ...(normalized?.presetALabel ? { preset_a_label: String(normalized.presetALabel).trim() } : {}),
      ...(normalized?.presetBLabel ? { preset_b_label: String(normalized.presetBLabel).trim() } : {}),
      ...(normalized?.fromAsOf ? { from_as_of: String(normalized.fromAsOf).trim() } : {}),
      ...(normalized?.toAsOf ? { to_as_of: String(normalized.toAsOf).trim() } : {}),
      ...(normalized?.window ? { window: String(normalized.window).trim() } : {}),
      ...(normalized?.noteContains ? { note_contains: String(normalized.noteContains).trim() } : {}),
      ...(normalized?.minDeltaMarketValue !== undefined && normalized?.minDeltaMarketValue !== null && normalized?.minDeltaMarketValue !== ""
        ? { min_delta_market_value: Number(normalized.minDeltaMarketValue) }
        : {}),
      ...(normalized?.minDeltaUnrealizedPl !== undefined && normalized?.minDeltaUnrealizedPl !== null && normalized?.minDeltaUnrealizedPl !== ""
        ? { min_delta_unrealized_pl: Number(normalized.minDeltaUnrealizedPl) }
        : {}),
    },
    responseType: "blob",
  });

  const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
  const contentDisposition = response.headers?.["content-disposition"] || "";
  const match = contentDisposition.match(/filename=([^;]+)/i);
  const filename = match ? match[1].replace(/"/g, "").trim() : "what_if_compare_history.csv";

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  return { filename };
}

export async function getWhatIfPresets() {
  const response = await axios.get(`${API_BASE}/simulate/presets`);
  return response.data?.presets ?? [];
}

export async function saveWhatIfPresets(presets) {
  const response = await axios.post(`${API_BASE}/simulate/presets`, {
    presets,
  });
  return response.data?.presets ?? [];
}

export async function getWhatIfRunHistory(options = 20) {
  const normalized = typeof options === "number" ? { limit: options } : options;
  const params = {
    limit: Number(normalized?.limit ?? 20),
  };
  if (normalized?.symbol) params.symbol = String(normalized.symbol).trim().toUpperCase();
  if (normalized?.action) params.action = String(normalized.action).trim().toLowerCase();
  if (normalized?.presetName) params.preset_name = String(normalized.presetName).trim();
  const response = await axios.get(`${API_BASE}/simulate/history`, { params });
  return response.data?.history ?? [];
}

export async function appendWhatIfRunHistory(entry) {
  const response = await axios.post(`${API_BASE}/simulate/history`, {
    ...entry,
  });
  return response.data ?? {};
}

export async function exportWhatIfRunHistoryCsv(options = {}) {
  const params = {
    limit: Number(options?.limit ?? 100),
  };
  if (options?.symbol) params.symbol = String(options.symbol).trim().toUpperCase();
  if (options?.action) params.action = String(options.action).trim().toLowerCase();
  if (options?.presetName) params.preset_name = String(options.presetName).trim();

  const response = await axios.get(`${API_BASE}/simulate/history/export`, {
    params,
    responseType: "blob",
  });

  const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
  const contentDisposition = response.headers?.["content-disposition"] || "";
  const match = contentDisposition.match(/filename=([^;]+)/i);
  const filename = match ? match[1].replace(/"/g, "").trim() : "what_if_run_history.csv";

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  return { filename, rowCountHint: Number(options?.limit ?? 100) };
}
